import React from "react";
import classnames from "classnames";
import difference from "lodash/difference";
import includes from "lodash/includes";
import intersection from "lodash/intersection";
import uniq from "lodash/uniq";
import PropTypes from "prop-types";
import { currentUserShape, portfolioShape } from "shapes";

import { groupBy } from "utils";

import { CompanyBlock, PortfolioBlock, SearchBlock } from "./components";

import styles from "./PortfolioSidebar.css";

const addEventListener = (func) => {
  document.addEventListener("click", func, false);
  document.addEventListener("touchend", func, false);
};
const removeEventListener = (func) => {
  document.removeEventListener("click", func, false);
  document.removeEventListener("touchend", func, false);
};

class PortfolioSidebar extends React.Component {
  constructor(props) {
    super(props);
    this.state = { keywords: "", selectedPortfolioIDs: [] };

    this.getPortfolios = this.getPortfolios.bind(this);
    this.handleCompanySelect = this.handleCompanySelect.bind(this);
    this.handlePortfolioSearch = this.handlePortfolioSearch.bind(this);
    this.handlePortfolioSelect = this.handlePortfolioSelect.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.handleUnselectAll = this.handleUnselectAll.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.isCompanySelected = this.isCompanySelected.bind(this);
  }

  static getDerivedStateFromProps({ isOpen, selectedPortfolioIDs }) {
    if (!isOpen) return { keywords: "", selectedPortfolioIDs };

    return null;
  }

  componentDidUpdate() {
    if (this.props.isOpen) {
      addEventListener(this.handleOutsideClick);
    } else {
      removeEventListener(this.handleOutsideClick);
    }
  }

  componentWillUnmount() {
    removeEventListener(this.handleOutsideClick);
  }

  handleApply() {
    const { onClose, onSelectPortfolioIDs } = this.props;
    const { selectedPortfolioIDs } = this.state;
    onClose();
    onSelectPortfolioIDs(selectedPortfolioIDs);
  }

  handleCompanySelect(companyID, portfolioIDs) {
    const { selectedPortfolioIDs } = this.state;
    if (this.isCompanySelected(companyID)) {
      this.setState({
        selectedPortfolioIDs: selectedPortfolioIDs.filter((s) => !includes(portfolioIDs, s)),
      });
    } else {
      this.setState({
        selectedPortfolioIDs: uniq([...selectedPortfolioIDs, ...portfolioIDs]),
      });
    }
  }

  handleOutsideClick(e) {
    const el = this.container;
    const { isOpen } = this.props;

    if (isOpen && el && !el.contains(e.target)) this.handleApply();
  }

  handlePortfolioSearch(keywords) {
    this.setState({ keywords });
  }

  handlePortfolioSelect(selectedPortfolioIDs) {
    this.setState({ selectedPortfolioIDs });
  }

  handleSelectAll() {
    this.setState({
      selectedPortfolioIDs: this.getPortfolios()
        .filter((p) => !p.fake)
        .map((p) => p.id),
    });
  }

  handleUnselectAll() {
    const { selectedPortfolioIDs } = this.state;
    this.setState({
      selectedPortfolioIDs: difference(
        selectedPortfolioIDs,
        this.getPortfolios().map((p) => p.id)
      ),
    });
  }

  getPortfolios() {
    const { portfolios } = this.props;
    const { keywords } = this.state;
    if (!keywords || keywords.length < 1) return portfolios;

    return portfolios.filter(
      (p) =>
        includes(p.name.toLowerCase(), keywords.toLowerCase()) ||
        includes(p.company.name.toLowerCase(), keywords.toLowerCase())
    );
  }

  isCompanySelected(companyID) {
    const { selectedPortfolioIDs } = this.state;
    const { portfolios } = this.props;
    const selectedPortfolios = portfolios.filter((p) => includes(selectedPortfolioIDs, p.id));

    return (
      selectedPortfolios.filter((p) => p.company.id === companyID).length ===
      this.getPortfolios()
        .filter((p) => p.company.id === companyID)
        .filter((p) => !p.fake).length
    );
  }

  render() {
    const {
      currentUser,
      isOpen,
      isOwner,
      onOpenPortfolioModal,
      onRequestAccessPortfolio,
      portfolios,
    } = this.props;
    const { selectedPortfolioIDs } = this.state;
    const { keywords } = this.state;
    const groupedPortfolios = groupBy(this.getPortfolios(), (p) => p.company.id);
    const isEditing = !!portfolios.find((p) => p.isEdit);
    const allPortfoliosSelected =
      intersection(
        this.getPortfolios()
          .map((p) => p.id)
          .filter((p) => !p.fake),
        selectedPortfolioIDs
      ).length === this.getPortfolios().filter((p) => !p.fake).length;
    const isInternalUser = currentUser.roleInCompany === "internal_user";

    return (
      <div
        className={classnames(styles.menu, { [styles.open]: isOpen })}
        ref={(ref) => {
          this.container = ref;
        }}
      >
        <SearchBlock
          isAllSelected={allPortfoliosSelected}
          keywords={keywords}
          nbSelectedPortfolios={selectedPortfolioIDs.length}
          onApply={this.handleApply}
          onSearch={this.handlePortfolioSearch}
          onSelectAll={this.handleSelectAll}
          onUnselectAll={this.handleUnselectAll}
        />
        <div className={styles.wrapper}>
          {Object.keys(groupedPortfolios).map((key) => (
            <CompanyBlock
              company={groupedPortfolios[key][0].company}
              isEditable={isOwner && !isInternalUser}
              isFake={groupedPortfolios[key].every((p) => p.fake)}
              isSelected={this.isCompanySelected(parseInt(key, 10))}
              key={key}
              logoUrl={groupedPortfolios[key][0].company.logoUrl}
              onCreatePortfolio={onOpenPortfolioModal}
              onSelectCompany={() =>
                this.handleCompanySelect(
                  parseInt(key, 10),
                  groupedPortfolios[key].filter((p) => !p.fake).map((p) => p.id)
                )
              }
              portfolioIDs={groupedPortfolios[key].map((p) => p.id)}
              selectedPortfolioIDs={selectedPortfolioIDs}
            >
              {groupedPortfolios[key].map((portfolio, pIndex) => (
                <React.Fragment key={`company_${key}_portfolio_${pIndex}`}>
                  <PortfolioBlock
                    {...portfolio}
                    isActive={
                      (includes(selectedPortfolioIDs, portfolio.id) && !isEditing) ||
                      portfolio.isEdit
                    }
                    isEditable={isOwner && !isInternalUser}
                    isFake={portfolio.fake}
                    isOwner={isOwner}
                    onEditPortfolio={onOpenPortfolioModal}
                    onRequestAccessPortfolio={() => onRequestAccessPortfolio(portfolio)}
                    onSelectPortfolio={this.handlePortfolioSelect}
                    requestedAccess={portfolio.requestedAccess}
                    selectedPortfolioIDs={selectedPortfolioIDs}
                  />
                  {groupedPortfolios[key].length !== pIndex + 1 && (
                    <div className={styles.separator} />
                  )}
                </React.Fragment>
              ))}
            </CompanyBlock>
          ))}
        </div>
      </div>
    );
  }
}

PortfolioSidebar.propTypes = {
  currentUser: PropTypes.shape(currentUserShape).isRequired,
  selectedPortfolioIDs: PropTypes.arrayOf(PropTypes.number),
  portfolios: PropTypes.arrayOf(PropTypes.shape(portfolioShape)),
  onClose: PropTypes.func.isRequired,
  onOpenPortfolioModal: PropTypes.func.isRequired,
  onRequestAccessPortfolio: PropTypes.func.isRequired,
  onSelectPortfolioIDs: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  isOwner: PropTypes.bool,
};

PortfolioSidebar.defaultProps = {
  portfolios: [],
};

export default PortfolioSidebar;
